import { Big } from "big.js";
import { match } from "ts-pattern";
import { type ZodMiniType, z } from "zod/mini";
import type { $RefinementCtx } from "zod/v4/core";
import type { Auction } from "@/queries/auction.ts";
import type { BidTarget } from "@/queries/auction-session.js";
import { currencyFormatter, numberFormatter } from "@/utils/format.ts";

type MaskedAmountSchemaOptions<TAllowEmpty extends boolean = false> = {
    allowEmpty?: TAllowEmpty;
    min?: number;
    max?: number;
};

type MaskedAmountSchema<TAllowEmpty extends boolean = false> = TAllowEmpty extends true
    ? ZodMiniType<number | null, string>
    : ZodMiniType<number, string>;

export const createMaskedAmountSchema = <TAllowEmpty extends boolean = false>(
    options?: MaskedAmountSchemaOptions<TAllowEmpty>,
): MaskedAmountSchema<TAllowEmpty> => {
    let baseSchema: ZodMiniType<string, string> = z
        .string()
        .check(z.regex(/^\d{1,3}(,?\d{3})*$/, "Required"));

    if (options?.allowEmpty) {
        baseSchema = z.union([baseSchema, z.literal("")]);
    }

    let schema: ZodMiniType<number | null, string> = z.pipe(
        baseSchema,
        z.transform((value) =>
            value === "" ? null : Number.parseInt(value.replace(/,/g, ""), 10),
        ),
    );

    const min = options?.min;
    const max = options?.max;

    if (min) {
        schema = schema.check(
            z.refine(
                (value) => value === null || value >= min,
                `Must be at least ${numberFormatter.format(min)}`,
            ),
        );
    }

    if (max) {
        schema = schema.check(
            z.refine(
                (value) => value === null || value <= max,
                `Must be at at most ${numberFormatter.format(max)}`,
            ),
        );
    }

    return schema as MaskedAmountSchema<TAllowEmpty>;
};

export const createOptionalTextSchema = <TOutput>(
    schema: ZodMiniType<TOutput, string>,
): ZodMiniType<TOutput | null, string | null | undefined> =>
    z.pipe(
        z.pipe(
            z.nullish(z.string()),
            z.transform((value) => (value ? value : null)),
        ),
        z.nullable(schema),
    );

export const getAmountFactor = (priceModel: Auction["priceModel"]): [number, number] =>
    match<Auction["priceModel"], [number, number]>(priceModel)
        .with("total", () => [1000, 1000])
        .with("per_acre", () => [100, 25])
        .exhaustive();

export const getMinimumBid = (
    target: BidTarget,
    index: number,
    config: Auction["config"],
    factor: number,
): number => {
    if (target === "global") {
        return 0;
    }

    const acreage = match(config)
        .with({ type: "single_parcel" }, (config) => {
            return config.parcel.acreage.toNumber();
        })
        .with({ type: "multi_parcel" }, (config) => {
            return config.parcels[index].acreage.toNumber();
        })
        .with({ type: "combo" }, (config) => {
            if (target === "parcel") {
                return config.parcels[index].acreage.toNumber();
            }

            if (target === "parcel_combination") {
                const combination = config.parcelCombinations[index];

                return config.parcels.reduce((acreage, parcel, index) => {
                    if (!combination.parcelIndices.includes(index)) {
                        return acreage;
                    }

                    return acreage + parcel.acreage.toNumber();
                }, 0);
            }

            return config.parcels[index].acreage.toNumber();
        })
        .with({ type: "choice" }, () => {
            return 0;
        })
        .exhaustive();

    return Math.ceil((acreage * 1000) / factor) * factor;
};

export const requireMinimumBid = (
    target: BidTarget,
    index: number,
    auction: Auction,
    factor: number,
    value: number | null,
    context: $RefinementCtx,
    path: (string | number)[],
): void => {
    const minimum =
        auction.sealedBid || auction.priceModel === "per_acre"
            ? 1000
            : getMinimumBid(target, index, auction.config, factor);

    if (value !== null && value < minimum) {
        context.addIssue({
            code: "custom",
            message: `Value must be a at least ${currencyFormatter.format(minimum)}`,
            path: path,
        });
    }
};

export const requireValidMultiple = (
    factor: number,
    value: number | null,
    context: $RefinementCtx,
    path: (string | number)[],
): void => {
    if (value !== null && value % factor !== 0) {
        context.addIssue({
            code: "custom",
            message: `Value must be a multiple of ${currencyFormatter.format(factor)}`,
            path: path,
        });
    }
};

export const decimalSchema = () =>
    z.pipe(
        z.string(),
        z.transform((value, context) => {
            try {
                return new Big(value);
            } catch {
                context.issues.push({
                    code: "custom",
                    message: "Invalid decimal",
                    input: value,
                });
                return z.NEVER;
            }
        }),
    );
